import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  writeBatch,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/firebase';
import type {
  NotificationCategory,
  InboxNotification,
} from '@/types/NotificationTypes';

// Re-export types for backward compatibility
export type { NotificationCategory, InboxNotification };

/**
 * Notification Inbox Store
 *
 * Firebase-native notification system using Firestore as source of truth.
 * Notifications are stored in users/{uid}/notifications subcollection.
 *
 * - Cloud Functions create notifications (no client writes)
 * - Users can mark as read or delete
 * - Real-time sync via onSnapshot listener
 */

interface NotificationInboxState {
  // Data (from Firestore)
  notifications: InboxNotification[];
  isLoading: boolean;
  error: string | null;

  // UI state (local only)
  isOpen: boolean;
  lastViewedAt: string | null;

  // Current subscription
  _currentUid: string | null;
  _unsubscribe: Unsubscribe | null;

  // Actions
  subscribe: (uid: string) => Unsubscribe;
  unsubscribe: () => void;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  removeNotification: (id: string) => Promise<void>;
  clearAll: () => Promise<void>;
  setIsOpen: (open: boolean) => void;
  toggleOpen: () => void;
}

export const useNotificationInbox = create<NotificationInboxState>()(
  devtools(
    (set, get) => ({
      notifications: [],
      isLoading: true,
      error: null,
      isOpen: false,
      lastViewedAt: null,
      _currentUid: null,
      _unsubscribe: null,

      subscribe: (uid: string) => {
        // Unsubscribe from previous if exists
        const { _unsubscribe: prevUnsub } = get();
        if (prevUnsub) {
          prevUnsub();
        }

        set({ isLoading: true, error: null, _currentUid: uid });

        const q = query(
          collection(db, `users/${uid}/notifications`),
          orderBy('createdAt', 'desc'),
          limit(50)
        );

        const unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            const notifications: InboxNotification[] = snapshot.docs.map((docSnap) => {
              const data = docSnap.data();
              return {
                id: docSnap.id,
                category: data.category,
                title: data.title,
                message: data.message,
                actionUrl: data.actionUrl,
                actionLabel: data.actionLabel,
                read: data.read,
                createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
                source: data.source,
                metadata: data.metadata,
              };
            });

            set({
              notifications,
              isLoading: false,
              error: null,
            });
          },
          (error) => {
            console.error('Notification subscription error:', error);
            set({
              isLoading: false,
              error: error.message,
            });
          }
        );

        set({ _unsubscribe: unsubscribe });
        return unsubscribe;
      },

      unsubscribe: () => {
        const { _unsubscribe } = get();
        if (_unsubscribe) {
          _unsubscribe();
          set({
            _unsubscribe: null,
            _currentUid: null,
            notifications: [],
            isLoading: false,
          });
        }
      },

      markAsRead: async (id: string) => {
        const { _currentUid } = get();
        if (!_currentUid) return;

        try {
          await updateDoc(doc(db, `users/${_currentUid}/notifications/${id}`), {
            read: true,
          });
        } catch (error) {
          console.error('Failed to mark notification as read:', error);
        }
      },

      markAllAsRead: async () => {
        const { _currentUid, notifications } = get();
        if (!_currentUid) return;

        const unreadNotifications = notifications.filter((n) => !n.read);
        if (unreadNotifications.length === 0) return;

        try {
          const batch = writeBatch(db);
          unreadNotifications.forEach((n) => {
            const ref = doc(db, `users/${_currentUid}/notifications/${n.id}`);
            batch.update(ref, { read: true });
          });
          await batch.commit();

          set({ lastViewedAt: new Date().toISOString() });
        } catch (error) {
          console.error('Failed to mark all notifications as read:', error);
        }
      },

      removeNotification: async (id: string) => {
        const { _currentUid } = get();
        if (!_currentUid) return;

        try {
          await deleteDoc(doc(db, `users/${_currentUid}/notifications/${id}`));
        } catch (error) {
          console.error('Failed to remove notification:', error);
        }
      },

      clearAll: async () => {
        const { _currentUid, notifications } = get();
        if (!_currentUid || notifications.length === 0) return;

        try {
          const batch = writeBatch(db);
          notifications.forEach((n) => {
            const ref = doc(db, `users/${_currentUid}/notifications/${n.id}`);
            batch.delete(ref);
          });
          await batch.commit();
        } catch (error) {
          console.error('Failed to clear all notifications:', error);
        }
      },

      setIsOpen: (open: boolean) => {
        set({ isOpen: open });
        if (open) {
          set({ lastViewedAt: new Date().toISOString() });
        }
      },

      toggleOpen: () => {
        const currentOpen = get().isOpen;
        set({ isOpen: !currentOpen });
        if (!currentOpen) {
          set({ lastViewedAt: new Date().toISOString() });
        }
      },
    }),
    { name: 'notification-inbox' }
  )
);

// Selectors
export const useInboxNotifications = () =>
  useNotificationInbox((state) => state.notifications);

export const useUnreadCount = () =>
  useNotificationInbox((state) => state.notifications.filter((n) => !n.read).length);

export const useInboxOpen = () =>
  useNotificationInbox((state) => state.isOpen);

export const useInboxLoading = () =>
  useNotificationInbox((state) => state.isLoading);

export const useInboxActions = () =>
  useNotificationInbox(
    useShallow((state) => ({
      subscribe: state.subscribe,
      unsubscribe: state.unsubscribe,
      markAsRead: state.markAsRead,
      markAllAsRead: state.markAllAsRead,
      removeNotification: state.removeNotification,
      clearAll: state.clearAll,
      setIsOpen: state.setIsOpen,
      toggleOpen: state.toggleOpen,
    }))
  );

// Category config for styling (kept for backward compatibility with NotificationTray)
export const NOTIFICATION_CATEGORY_CONFIG: Record<NotificationCategory, {
  icon: string;
  color: string;
  bgColor: string;
}> = {
  study_reminder: {
    icon: 'BookOpen',
    color: 'text-primary-600',
    bgColor: 'bg-primary-50',
  },
  progress_update: {
    icon: 'TrendingUp',
    color: 'text-success-600',
    bgColor: 'bg-success-50',
  },
  achievement: {
    icon: 'Award',
    color: 'text-warning-600',
    bgColor: 'bg-warning-50',
  },
  celebration: {
    icon: 'Award',
    color: 'text-celebrate-700',
    bgColor: 'bg-celebrate-100',
  },
  trial_offer: {
    icon: 'Gift',
    color: 'text-accent-600',
    bgColor: 'bg-accent-50',
  },
  exam_countdown: {
    icon: 'Clock',
    color: 'text-secondary-600',
    bgColor: 'bg-secondary-50',
  },
  tip: {
    icon: 'Lightbulb',
    color: 'text-primary-600',
    bgColor: 'bg-primary-50',
  },
  system: {
    icon: 'Bell',
    color: 'text-neutral-600',
    bgColor: 'bg-neutral-50',
  },
};
